import type { VenueWeekdayHours, Weekday } from './operatingHours';
import { isOnSlotGrid, slotTimeOptions, snapToSlotGrid } from './slotTimeGrid';

export type EditableWeekdayHours = {
  weekday: Weekday;
  openTime: string;
  closeTime: string;
  closed: boolean;
};

const WEEKDAYS: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

/** A close of 00:00 is midnight at the end of the same day, not an overnight wrap (ADR 0080). */
export const MIDNIGHT_CLOSE = '00:00';

/** Whether a day may close at `closeTime` after opening at `openTime`. */
export function closesAfterOpening(openTime: string, closeTime: string): boolean {
  return closeTime === MIDNIGHT_CLOSE || openTime < closeTime;
}

/**
 * The closing times a day opening at `openTime` may pick: every later grid time, then midnight
 * last. Before an open is picked every grid time after 00:00 is offered.
 */
export function closeTimeOptions(openTime: string, granularityMinutes: number): string[] {
  const laterTimes = slotTimeOptions(granularityMinutes).filter(
    (time) => time !== MIDNIGHT_CLOSE && (!openTime || time > openTime),
  );
  return [...laterTimes, MIDNIGHT_CLOSE];
}

/**
 * Snap every open and close down onto a new granularity's grid, because a coarser grid can strand
 * a picked time its dropdown no longer offers. Blanks stay blank, and a close that no longer
 * follows its snapped open is cleared, as picking a later open clears it.
 */
export function snapEditableWeekdayHours(
  weekdayHours: EditableWeekdayHours[],
  granularityMinutes: number,
): EditableWeekdayHours[] {
  return weekdayHours.map((row) => {
    const openTime = snapToSlotGrid(row.openTime, granularityMinutes);
    const closeTime = snapToSlotGrid(row.closeTime, granularityMinutes);
    return {
      ...row,
      openTime,
      closeTime: closesAfterOpening(openTime, closeTime) ? closeTime : '',
    };
  });
}

export function emptyEditableWeekdayHours(): EditableWeekdayHours[] {
  return WEEKDAYS.map((weekday) => ({
    weekday,
    openTime: '',
    closeTime: '',
    closed: false,
  }));
}

export function toEditableWeekdayHours(entry: VenueWeekdayHours): EditableWeekdayHours {
  return {
    weekday: entry.weekday,
    openTime: entry.openTime ?? '',
    closeTime: entry.closeTime ?? '',
    closed: entry.openTime === null && entry.closeTime === null,
  };
}

export function toRequestWeekdayHours(row: EditableWeekdayHours): VenueWeekdayHours {
  return {
    weekday: row.weekday,
    openTime: row.closed ? null : row.openTime,
    closeTime: row.closed ? null : row.closeTime,
  };
}

/** The same rule the server enforces: both ends on the grid, and closing after opening. */
export function validateEditableWeekdayHours(
  weekdayHours: EditableWeekdayHours[],
  granularityMinutes: number,
): 'missing_interval' | 'off_grid_interval' | 'invalid_interval' | null {
  for (const row of weekdayHours) {
    if (row.closed) continue;
    if (!row.openTime || !row.closeTime) return 'missing_interval';
    if (
      !isOnSlotGrid(row.openTime, granularityMinutes) ||
      !isOnSlotGrid(row.closeTime, granularityMinutes)
    ) {
      return 'off_grid_interval';
    }
    if (!closesAfterOpening(row.openTime, row.closeTime)) return 'invalid_interval';
  }
  return null;
}
